
import { processWildcards, addDatePatternWithWildcards } from './wildcards.js';
import { MONTH_NAMES } from '../../search/smart-date-recognizer/constants.js';

/**
 * Add date highlight patterns to highlight conditions (for text highlighting only, not search)
 * This function generates patterns for highlighting dates in filenames/paths
 * @param {Array} highlightConditions - Existing highlight conditions from getMatchConditions
 * @param {string} queryOriginal - Original search query
 * @returns {Array} - Highlight conditions with date patterns added
 */
export function addDateHighlightPatterns(highlightConditions, queryOriginal) {
    if (!queryOriginal || !queryOriginal.trim()) {
        return highlightConditions || [];
    }

    // Parse date from query via smart-date-recognizer
    let dateFromQuery = null;
    let dateToQuery = null;
    let dateVariants = null;

    try {
        if (typeof window.recognizeDate === 'function') {
            const recognized = window.recognizeDate(queryOriginal);
            if (recognized) {
                if (recognized.variants && Array.isArray(recognized.variants) && recognized.variants.length > 0) {
                    dateVariants = recognized.variants;
                } else if (recognized.from || recognized.to) {
                    dateFromQuery = recognized.from || null;
                    dateToQuery = recognized.to || null;
                }
            }
        }
    } catch(_) {}

    // Generate smart text date patterns for highlighting
    const smartTextDatePatterns = [];
    const smartTextDatePatternsSet = new Set();

    if (dateFromQuery || dateToQuery || dateVariants) {
        if (dateFromQuery && dateToQuery) {
            const fromParts = dateFromQuery.split('-');
            const toParts = dateToQuery.split('-');
            if (fromParts.length === 3 && toParts.length === 3) {
                // If same month-day (e.g., "3012" = 30 Dec any year)
                if (fromParts[1] === toParts[1] && fromParts[2] === toParts[2]) {
                    const month = parseInt(fromParts[1], 10);
                    const day = parseInt(fromParts[2], 10);
                    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                        const dayStr = day.toString();
                        const dayPadded = String(day).padStart(2, '0');
                        const monthPadded = String(month).padStart(2, '0');
                        const separators = '[\\s\\-\\./,]';
                        
                        // Numeric formats: DD.MM, DD-MM, DD/MM, DD MM
                        const patterns = [
                            `${dayPadded}${separators}${monthPadded}`,
                            `${monthPadded}${separators}${dayPadded}`,
                            `${monthPadded}${dayPadded}`,
                            `${monthPadded}[CcMm]${dayPadded}`,
                            `${monthPadded}[CcMm]${dayPadded}[A-Za-z]?`,
                            `\\d{2}${monthPadded}[CcMm]${dayPadded}[A-Za-z]?`
                        ];
                        if (dayStr !== dayPadded) {
                            patterns.push(`${dayStr}${separators}${monthPadded}`);
                        }
                        
                        for (const patternStr of patterns) {
                            // Process wildcards in date patterns
                            const processedPattern = processWildcards(patternStr);
                            if (!smartTextDatePatternsSet.has(processedPattern)) {
                                smartTextDatePatterns.push({ pattern: processedPattern, boost: 1, flags: 'i' });
                                smartTextDatePatternsSet.add(processedPattern);
                            }
                        }
                        
                        // Text month formats (if MONTH_NAMES available)
                        if (MONTH_NAMES && MONTH_NAMES[month]) {
                            const monthData = MONTH_NAMES[month];
                            const allMonthNames = [];
                            for (const lang of Object.keys(monthData)) {
                                allMonthNames.push(...monthData[lang]);
                            }
                            const uniqueMonths = [...new Set(allMonthNames)];
                            const escRx = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                            const monthPattern = uniqueMonths.map(m => escRx(m)).join('|');
                            
                            const textPatterns = [
                                `${dayStr}${separators}?(?:${monthPattern})`,
                                `${dayPadded}${separators}?(?:${monthPattern})`,
                                `(?:${monthPattern})${separators}?${dayStr}`,
                                `(?:${monthPattern})${separators}?${dayPadded}`
                            ];
                            
                            for (const patternStr of textPatterns) {
                                addDatePatternWithWildcards(smartTextDatePatterns, smartTextDatePatternsSet, patternStr);
                            }
                        }
                    }
                } else if (fromParts[0] === toParts[0] && fromParts[1] === toParts[1]) {
                    // Same year-month (e.g., "2002" = year 2002, month 02)
                    const year = parseInt(fromParts[0], 10);
                    const month = parseInt(fromParts[1], 10);
                    if (year >= 1900 && year <= 2100 && month >= 1 && month <= 12) {
                        // Add year pattern for filename highlighting
                        addDatePatternWithWildcards(smartTextDatePatterns, smartTextDatePatternsSet, String(year));
                        
                        // Add shortened year pattern (last 2 digits) for filenames that use shortened format
                        // e.g., "1953" → also highlight "53"
                        if (year >= 1900 && year <= 2099) {
                            const shortYear = String(year).slice(-2);
                            addDatePatternWithWildcards(smartTextDatePatterns, smartTextDatePatternsSet, shortYear);
                        }
                        
                        // Text month formats
                        if (MONTH_NAMES && MONTH_NAMES[month]) {
                            const monthData = MONTH_NAMES[month];
                            const allMonthNames = [];
                            for (const lang of Object.keys(monthData)) {
                                allMonthNames.push(...monthData[lang]);
                            }
                            const uniqueMonths = [...new Set(allMonthNames)];
                            const escRx = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                            const monthPattern = uniqueMonths.map(m => escRx(m)).join('|');
                            
                            const patternStr = `(?:${monthPattern})`;
                            addDatePatternWithWildcards(smartTextDatePatterns, smartTextDatePatternsSet, patternStr);
                        }
                    }
                } else if (fromParts[0] === toParts[0]) {
                    // Same year, different months (e.g., "1953" = year 1953, any month)
                    const year = parseInt(fromParts[0], 10);
                    if (year >= 1900 && year <= 2100) {
                        // Add full year pattern for filename highlighting
                        addDatePatternWithWildcards(smartTextDatePatterns, smartTextDatePatternsSet, String(year));
                        
                        // Add shortened year pattern (last 2 digits) for filenames that use shortened format
                        // e.g., "1953" → also highlight "53"
                        if (year >= 1900 && year <= 2099) {
                            const shortYear = String(year).slice(-2);
                            addDatePatternWithWildcards(smartTextDatePatterns, smartTextDatePatternsSet, shortYear);
                        }
                    }
                }
            }
        }
        
        // Handle dateVariants (ambiguous interpretations)
        if (dateVariants && Array.isArray(dateVariants) && dateVariants.length > 0) {
            for (const variant of dateVariants) {
                if (variant.from && variant.to) {
                    const variantFromParts = variant.from.split('-');
                    const variantToParts = variant.to.split('-');
                    if (variantFromParts.length === 3 && variantToParts.length === 3) {
                        if (variantFromParts[1] === variantToParts[1] && variantFromParts[2] === variantToParts[2]) {
                            const month = parseInt(variantFromParts[1], 10);
                            const day = parseInt(variantFromParts[2], 10);
                            if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                                const dayStr = day.toString();
                                const dayPadded = String(day).padStart(2, '0');
                                const monthPadded = String(month).padStart(2, '0');
                                const separators = '[\\s\\-\\./,]';
                                
                                const patterns = [
                                    `${dayPadded}${separators}${monthPadded}`,
                                    `${monthPadded}${separators}${dayPadded}`,
                                    `${monthPadded}${dayPadded}`
                                ];
                                if (dayStr !== dayPadded) {
                                    patterns.push(`${dayStr}${separators}${monthPadded}`);
                                }
                                
                                for (const patternStr of patterns) {
                                    addDatePatternWithWildcards(smartTextDatePatterns, smartTextDatePatternsSet, patternStr);
                                }
                                
                                // Text month formats
                                if (MONTH_NAMES && MONTH_NAMES[month]) {
                                    const monthData = MONTH_NAMES[month];
                                    const allMonthNames = [];
                                    for (const lang of Object.keys(monthData)) {
                                        allMonthNames.push(...monthData[lang]);
                                    }
                                    const uniqueMonths = [...new Set(allMonthNames)];
                                    const escRx = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                    const monthPattern = uniqueMonths.map(m => escRx(m)).join('|');
                                    
                                    const textPatterns = [
                                        `${dayStr}${separators}?(?:${monthPattern})`,
                                        `${dayPadded}${separators}?(?:${monthPattern})`,
                                        `(?:${monthPattern})${separators}?${dayStr}`,
                                        `(?:${monthPattern})${separators}?${dayPadded}`
                                    ];
                                    
                                    for (const patternStr of textPatterns) {
                                        addDatePatternWithWildcards(smartTextDatePatterns, smartTextDatePatternsSet, patternStr);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    
    // Add smart text date patterns to highlight conditions (for highlighting only)
    if (smartTextDatePatterns.length > 0) {
        if (window.HyperLog && debugMode === '1') { try { window.HyperLog.debug('[FilterManager] Date highlight patterns generated', { smartPatternsCount: smartTextDatePatterns.length, patterns: smartTextDatePatterns.map(p => p.pattern), queryOriginal, dateFromQuery, dateToQuery }); } catch(_){} }
        
        if (Array.isArray(highlightConditions) && highlightConditions.length > 0) {
            let addedToAny = false;
            
            // Check for OR wrapper structure (auto-exact phrase)
            if (highlightConditions[0] && highlightConditions[0].condition === 'or' && highlightConditions[0].value && Array.isArray(highlightConditions[0].value)) {
                const isOrWrapper = highlightConditions[0].value[0] && highlightConditions[0].value[0].condition &&
                    (highlightConditions[0].value[0].condition === 'and' || highlightConditions[0].value[0].condition === 'or');
                
                if (isOrWrapper) {
                    // OR wrapper: add date patterns to each AND condition's OR subconditions
                    for (const nestedCond of highlightConditions[0].value) {
                        if (nestedCond.condition === 'and' && nestedCond.value && Array.isArray(nestedCond.value)) {
                            for (const subCond of nestedCond.value) {
                                if (subCond.condition === 'or' && subCond.value && Array.isArray(subCond.value)) {
                                    // Build set of existing patterns to avoid duplicates
                                    const existingPatterns = new Set();
                                    for (const existingPattern of subCond.value) {
                                        const patternStr = (typeof existingPattern === 'object' && existingPattern.pattern) ? existingPattern.pattern : String(existingPattern);
                                        existingPatterns.add(patternStr);
                                    }
                                    
                                    // Add smart text date patterns at the end (for highlighting only)
                                    for (const smartPattern of smartTextDatePatterns) {
                                        const patternStr = smartPattern.pattern;
                                        if (!existingPatterns.has(patternStr)) {
                                            subCond.value.push(smartPattern);
                                            existingPatterns.add(patternStr);
                                            addedToAny = true;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            
            // Check for direct AND condition structure
            if (!addedToAny && highlightConditions[0] && highlightConditions[0].condition === 'and' && highlightConditions[0].value && Array.isArray(highlightConditions[0].value)) {
                for (const subCond of highlightConditions[0].value) {
                    if (subCond.condition === 'or' && subCond.value && Array.isArray(subCond.value)) {
                        // Build set of existing patterns to avoid duplicates
                        const existingPatterns = new Set();
                        for (const existingPattern of subCond.value) {
                            const patternStr = (typeof existingPattern === 'object' && existingPattern.pattern) ? existingPattern.pattern : String(existingPattern);
                            existingPatterns.add(patternStr);
                        }
                        
                        // Add smart text date patterns at the end (for highlighting only)
                        let addedCount = 0;
                        for (const smartPattern of smartTextDatePatterns) {
                            const patternStr = smartPattern.pattern;
                            if (!existingPatterns.has(patternStr)) {
                                subCond.value.push(smartPattern);
                                existingPatterns.add(patternStr);
                                addedCount++;
                                addedToAny = true;
                            }
                        }
                        if (window.HyperLog && debugMode === '1') { try { window.HyperLog.debug('[FilterManager] Added date highlight patterns', { smartPatternsCount: smartTextDatePatterns.length, addedCount, finalPatterns: subCond.value.map(p => (typeof p === 'object' && p.pattern) ? p.pattern : String(p)) }); } catch(_){} }
                        break;
                    }
                }
            }
            
            if (!addedToAny) {
                // If no AND condition structure, create one with date patterns
                if (window.HyperLog && debugMode === '1') { try { window.HyperLog.debug('[FilterManager] No AND condition found, creating new structure for date highlight patterns', { highlightConditionsLength: highlightConditions.length }); } catch(_){} }
                highlightConditions = [{
                    condition: 'and',
                    value: [
                        {
                            condition: 'or',
                            value: smartTextDatePatterns
                        }
                    ]
                }];
            }
        } else {
            // If no highlight conditions, create new ones with date patterns
            if (window.HyperLog && debugMode === '1') { try { window.HyperLog.debug('[FilterManager] No highlight conditions, creating new with date patterns'); } catch(_){} }
            highlightConditions = [{
                condition: 'and',
                value: [
                    {
                        condition: 'or',
                        value: smartTextDatePatterns
                    }
                ]
            }];
        }
    }
    
    return highlightConditions || [];
}

